import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { useTimeFormat } from "@/hooks/useTimeFormat";
import BreaksEditor, { validateBreaks } from "@/components/BreaksEditor";

const ABSENCE_REASONS = ["vacation", "sick", "training", "day_off", "private", "other"];
const todayIso = () => new Date().toISOString().slice(0, 10);

const DAYS = [0, 1, 2, 3, 4, 5, 6];

export default function Employees() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const confirm = useConfirm();
  const tf = useTimeFormat();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [absences, setAbsences] = useState([]);

  // Upcoming absences per employee (end_date >= today, sorted by start_date).
  const upcomingByEmployee = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const map = {};
    for (const a of absences) {
      if (!a.end_date || a.end_date < today) continue;
      (map[a.employee_id] = map[a.employee_id] || []).push(a);
    }
    Object.values(map).forEach((list) => list.sort((x, y) => x.start_date.localeCompare(y.start_date)));
    return map;
  }, [absences]);

  const [absenceDialog, setAbsenceDialog] = useState(null); // { employee } | null
  const [absForm, setAbsForm] = useState({ start_date: "", end_date: "", reason: "vacation", note: "" });
  const openAbsence = (employee) => {
    setAbsForm({ start_date: todayIso(), end_date: todayIso(), reason: "vacation", note: "" });
    setAbsenceDialog({ employee });
  };
  const submitAbsence = async () => {
    try {
      await api.post("/absences", { ...absForm, employee_id: absenceDialog.employee.id });
      toast.success("OK");
      setAbsenceDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const [open, setOpen] = useState(false);
  const [linkUser, setLinkUser] = useState(true);
  const [form, setForm] = useState({
    first_name: "", last_name: "", role: "Mechanic",
    user_id: null, email: "", password: "",
    working_days: [0, 1, 2, 3, 4], start_time: "08:00", end_time: "17:00",
    break_start: null, break_end: null, breaks: [], active: true,
    planning_visible: true,
  });

  const load = async () => {
    const [e, u, a] = await Promise.all([api.get("/employees"), api.get("/users"), api.get("/absences")]);
    setItems(e.data); setUsers(u.data); setAbsences(a.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      let userId = form.user_id;
      if (linkUser && form.email && form.password) {
        const { data } = await api.post("/users", {
          email: form.email, password: form.password,
          name: `${form.first_name} ${form.last_name}`, role: "mechanic",
        });
        userId = data.id;
      }
      const payload = {
        first_name: form.first_name, last_name: form.last_name, role: form.role,
        user_id: userId, working_days: form.working_days,
        start_time: form.start_time, end_time: form.end_time,
        // No legacy single-break fields — the new BreaksEditor is the single
        // source of truth.  An empty `breaks` list means "inherit from
        // workshop settings" (see backend resolve_employee_schedule).
        breaks: form.breaks || [],
        active: form.active, planning_visible: form.planning_visible,
      };
      await api.post("/employees", payload);
      toast.success("OK");
      setOpen(false);
      setForm({
        first_name: "", last_name: "", role: "Mechanic",
        user_id: null, email: "", password: "",
        working_days: [0,1,2,3,4], start_time: "08:00", end_time: "17:00",
        break_start: null, break_end: null, breaks: [], active: true,
        planning_visible: true,
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!(await confirm())) return;
    await api.delete(`/employees/${id}`);
    load();
  };

  const togglePlanningVisible = async (e) => {
    await api.patch(`/employees/${e.id}`, { planning_visible: !(e.planning_visible !== false) });
    load();
  };

  const weekdaysL = i18n.getResource(i18n.language, "translation", "weekdays") || ["Mo","Tu","We","Th","Fr","Sa","Su"];
  const canEdit = user?.role === "owner" || user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("employees.title")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">{t("employees.title")}</h1>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-500 hover:bg-blue-600" data-testid="new-employee-btn"><Plus className="h-4 w-4 mr-1" /> {t("common.new")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#21252E] border-[#2E3241] text-white max-w-xl">
              <DialogHeader><DialogTitle>{t("common.new")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">{t("employees.first_name")}</Label>
                  <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="bg-[#171B24] border-[#2E3241]" data-testid="emp-first-name" /></div>
                <div><Label className="text-xs">{t("employees.last_name")}</Label>
                  <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="bg-[#171B24] border-[#2E3241]" data-testid="emp-last-name" /></div>
                <div><Label className="text-xs">{t("employees.role")}</Label>
                  <Select value={form.role || "Mechanic"} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="bg-[#171B24] border-[#2E3241]" data-testid="emp-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                      <SelectItem value="Mechanic">{t("employees.role_mechanic")}</SelectItem>
                      <SelectItem value="Admin">{t("employees.role_admin")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 flex items-center justify-between border border-[#2E3241] rounded-lg p-3">
                  <div>
                    <div className="text-sm font-medium">{t("employees.user_account")}</div>
                    <div className="text-xs text-zinc-500">{t("employees.user_account_hint")}</div>
                  </div>
                  <Switch checked={linkUser} onCheckedChange={setLinkUser} />
                </div>
                {linkUser ? (
                  <>
                    <div><Label className="text-xs">{t("common.email")}</Label>
                      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="bg-[#171B24] border-[#2E3241]" data-testid="emp-email" /></div>
                    <div><Label className="text-xs">{t("auth.password")}</Label>
                      <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="bg-[#171B24] border-[#2E3241]" data-testid="emp-password" /></div>
                  </>
                ) : (
                  <div className="col-span-2">
                    <Label className="text-xs">Existing User</Label>
                    <Select value={form.user_id || ""} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                      <SelectTrigger className="bg-[#171B24] border-[#2E3241]"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                        {users.filter((u) => u.role === "mechanic").map((u) => <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="text-xs">{t("employees.working_days")}</Label>
                  <div className="flex gap-1 mt-1">
                    {DAYS.map((d) => (
                      <button key={d} type="button"
                        onClick={() => setForm({
                          ...form,
                          working_days: form.working_days.includes(d)
                            ? form.working_days.filter((x) => x !== d)
                            : [...form.working_days, d].sort(),
                        })}
                        className={`h-8 w-10 rounded text-xs font-medium border ${
                          form.working_days.includes(d)
                            ? "bg-blue-500 text-white border-blue-500"
                            : "bg-[#171B24] text-zinc-400 border-[#2E3241]"
                        }`}>
                        {weekdaysL[d]}
                      </button>
                    ))}
                  </div>
                </div>
                <div><Label className="text-xs">{t("employees.start")}</Label>
                  <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    className="bg-[#171B24] border-[#2E3241]" data-testid="emp-start-time" /></div>
                <div><Label className="text-xs">{t("employees.end")}</Label>
                  <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    className="bg-[#171B24] border-[#2E3241]" data-testid="emp-end-time" /></div>
                <div className="col-span-2">
                  <Label className="text-xs">{t("settings.breaks")}</Label>
                  <BreaksEditor
                    value={form.breaks || []}
                    onChange={(v) => setForm({ ...form, breaks: v })}
                    workingDays={form.working_days || [0,1,2,3,4]}
                    startTime={form.start_time || "08:00"}
                    endTime={form.end_time || "17:00"}
                    emptyHint={t("settings.inherit_from_workshop")}
                  />
                </div>
                <div className="col-span-2 flex items-center justify-between border border-[#2E3241] rounded-lg p-3">
                  <div>
                    <div className="text-sm font-medium">{t("employees.show_in_planning")}</div>
                    <div className="text-xs text-zinc-500">{t("employees.show_in_planning_hint")}</div>
                  </div>
                  <Switch
                    checked={!!form.planning_visible}
                    onCheckedChange={(v) => setForm({ ...form, planning_visible: v })}
                    data-testid="emp-planning-visible-toggle"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                <Button onClick={submit}
                  disabled={validateBreaks(form.breaks || [], form.start_time || "08:00", form.end_time || "17:00", t).errors.length > 0}
                  className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
                  data-testid="create-employee-submit">{t("common.create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="text-zinc-500 col-span-full">{t("common.empty")}</div>}
        {items.map((e) => (
          <div key={e.id} className="surface rounded-xl p-5" data-testid={`employee-card-${e.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display font-semibold text-lg">{e.first_name} {e.last_name}</div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-0.5">
                  {e.role ? t(`employees.role_${e.role.toLowerCase()}`, { defaultValue: e.role }) : ""}
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1">
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => togglePlanningVisible(e)}
                    title={t("employees.show_in_planning")}
                    className={e.planning_visible === false ? "text-zinc-600 hover:text-emerald-400" : "text-emerald-400 hover:text-zinc-400"}
                    data-testid={`employee-planning-toggle-${e.id}`}
                  >
                    {e.planning_visible === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => del(e.id)} className="text-zinc-400 hover:text-red-400" data-testid={`employee-del-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
            <div className="mt-4 space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-zinc-500">{t("employees.daily_capacity")}</span>
                <span className="font-mono-stat text-zinc-200" data-testid={`employee-capacity-${e.id}`}>
                  {tf.format(e.effective_daily_capacity_aw ?? e.daily_capacity_aw ?? 0)}
                </span></div>
              <div className="flex justify-between"><span className="text-zinc-500">{t("employees.start")}–{t("employees.end")}</span>
                <span className="font-mono-stat">{e.start_time} – {e.end_time}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">{t("employees.working_days")}</span>
                <span className="text-zinc-300">{e.working_days?.map((d) => weekdaysL[d]).join(" ")}</span></div>
            </div>
            {(canEdit || (upcomingByEmployee[e.id] || []).length > 0) && (
              <div className="mt-3 pt-3 border-t border-[#2E3241]" data-testid={`employee-upcoming-absences-${e.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] uppercase tracking-widest text-zinc-500">
                    {t("employees.upcoming_absences")}
                  </div>
                  {canEdit && (
                    <button type="button" onClick={() => openAbsence(e)}
                      className="inline-flex items-center gap-1 text-[11px] text-blue-400 hover:text-blue-300"
                      data-testid={`employee-add-absence-${e.id}`}>
                      <Plus className="h-3 w-3" /> {t("absence.new")}
                    </button>
                  )}
                </div>
                {(upcomingByEmployee[e.id] || []).length === 0 ? (
                  <p className="text-[11px] text-zinc-500 italic">{t("common.empty")}</p>
                ) : (
                  <ul className="space-y-1 text-xs">
                    {upcomingByEmployee[e.id].slice(0, 3).map((a) => (
                      <li key={a.id} className="flex items-center justify-between gap-2 rounded bg-red-500/10 border border-red-500/20 px-2 py-1"
                          data-testid={`absence-item-${a.id}`}>
                        <span className="text-red-200 truncate">{t(`absence.reasons.${a.reason}`)}</span>
                        <span className="font-mono-stat text-red-300/80 shrink-0">{a.start_date}{a.start_date !== a.end_date ? ` → ${a.end_date}` : ""}</span>
                      </li>
                    ))}
                    {upcomingByEmployee[e.id].length > 3 && (
                      <li className="text-[11px] text-zinc-500">+{upcomingByEmployee[e.id].length - 3} {t("common.more", { defaultValue: "more" })}</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Absence mini-dialog (mirrors the one in Wochenplanung) */}
      <Dialog open={!!absenceDialog} onOpenChange={(o) => !o && setAbsenceDialog(null)}>
        <DialogContent className="bg-[#21252E] border-[#2E3241] text-white">
          <DialogHeader>
            <DialogTitle>
              {t("absence.new")} — {absenceDialog?.employee.first_name} {absenceDialog?.employee.last_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("absence.start")}</Label>
                <Input type="date" value={absForm.start_date}
                  onChange={(ev) => setAbsForm({ ...absForm, start_date: ev.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" data-testid="employee-absence-start" />
              </div>
              <div>
                <Label className="text-xs">{t("absence.end")}</Label>
                <Input type="date" value={absForm.end_date}
                  onChange={(ev) => setAbsForm({ ...absForm, end_date: ev.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" data-testid="employee-absence-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("absence.reason")}</Label>
              <Select value={absForm.reason} onValueChange={(v) => setAbsForm({ ...absForm, reason: v })}>
                <SelectTrigger className="bg-[#171B24] border-[#2E3241]" data-testid="employee-absence-reason"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                  {ABSENCE_REASONS.map((r) => <SelectItem key={r} value={r}>{t(`absence.reasons.${r}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("absence.note")}</Label>
              <Input value={absForm.note}
                onChange={(ev) => setAbsForm({ ...absForm, note: ev.target.value })}
                className="bg-[#171B24] border-[#2E3241]" data-testid="employee-absence-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAbsenceDialog(null)}>{t("common.cancel")}</Button>
            <Button
              onClick={submitAbsence}
              disabled={!absForm.start_date || !absForm.end_date || absForm.end_date < absForm.start_date}
              className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
              data-testid="employee-absence-submit">
              <Plus className="h-4 w-4 mr-1" /> {t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
